<template>
  <div>
    <Navbar :isProfile="$route.path === '/profile-settings' || $route.path === '/account-settings'">
      <v-container
        pt-1
        :class="{'singleAppbar': $vuetify.breakpoint.smAndDown, 'doubleAppbar': $vuetify.breakpoint.mdAndUp}"
      >
        <v-banner
          v-if="Object.keys(organization).length && !organization.email_verified"
          class="mx-0 banner-style"
          app
          outlined
          single-line
        >
          <v-row align="center" justify="center">
            <v-icon class="mx-2" slot="icon" color="rgba(241, 101, 8, 0.986)" size="30">
              warning_amber
            </v-icon>
            <h4 class="mr-2">
              {{ $vuetify.lang.t("$vuetify.registration.resend_verification_email_msg_txt_2") }}
            </h4>
            <v-btn
              color="primary"
              small
              outlined
              @click="handleSubmitClick()"
              :loading="emailSendLoading"
            >
              {{ $vuetify.lang.t("$vuetify.registration.resend_verification_email_btn_txt") }}
            </v-btn>
          </v-row>
        </v-banner>
        <v-breadcrumbs class="pt-14 px-3" :items="crumbs">
          <template v-slot:divider>
            <v-icon>mdi-slash-forward</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <router-link
              v-if="!item.disabled"
              :to="item.to"
              class="text-decoration-none"
            >
              {{ item.text }}
            </router-link>
            <span v-else class="grey--text text--darken-1 font-weight-medium">
              {{ item.text }}
            </span>
          </template>
        </v-breadcrumbs>

        <v-main class="pt-1 pl-1">
          <router-view></router-view>
        </v-main>
        <MainFooter />
      </v-container>
    </Navbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Navbar from "@/components/TheNavbar";
import getEnv from "@/config/env";
import {
  LOAD_ORGANIZATION,
  SWITCH_ACCOOUNT,
  REFRESHTOKEN,
  LOAD_ACCOUNT,
  LOGOUT,
  RESEND_VERIFICATION_EMAIL,
  ADD_ALERT,
  LOAD_PROJECT_BY_ID,
  LOAD_SOLUTION_BY_ID
} from "@/store/_actiontypes";
import MainFooter from "@/components/layout/MainFooter.vue";

export default {
  components: {
    Navbar,
    MainFooter,
  },

  data() {
    return {
      emailSendLoading: false,
      // currentTab: 'details', // default tab when click on breadcrum project name
      validTabs: [
        'details',
        'settings',
        'insights',
        'setup',
        'datasets',
        'mairaSetup',
        'mairaDatasets',
        'mairaProfiles',
        'mairaSetting',
        'playground'
      ],
    };
  },
  created() {
    const { pid ,sid} = this.$route.query;
    if (pid) {
      this.loadProjectIfNeeded(pid);
    }
    if (sid) { 
      this.loadSolutionIfNeeded(sid);
    }
  },

  computed: {
    ...mapState({
      organization: (state) => state.account.organization,
      project: (state) => state.project.selectedProject,
      dataset: (state) => state.project.selectedGptDataset,
      solution:(state) => state.solution.selectedSolution,
      notifications: (state) => (state.notification && state.notification.notifications) || []
    }),

    crumbs() {
      const { path, query } = this.$route;
      const crumbs = [];
      const isProjectPage =( path.includes('/projects') || query.pid) && !query.sview;
      const isJobPage = path.includes('/jobs') ;
      const isMembersPage = path.includes('/members');
      const isCv = path.includes('/CV');
      const isSolutionsPage = path.includes('/solutions') || query.sview;
      const isProfileSettingsPage = path.includes('/profile-settings')
      const isNotificationsPage = path.includes('/notifications');
      const isSubscriptionPlansPage = path.includes('/subscription-plans');
      const isAllowedIPsPage = path.includes('/allowed-ips');
      
      if (isProjectPage) {
        // 1. Always start with Projects
        crumbs.push({
          text: this.$vuetify.lang.t("$vuetify.tab.solutions_projects_tab"),
          to: { path: '/projects', query: { stype: query.stype } },
          disabled: false,
        });
        // If creating a new project
        if (path === '/projects/create-project') {
          crumbs.push({
            text:  this.$vuetify.lang.t("$vuetify.project.create_project_btn_txt"),
            to: '',
            disabled: true,
          });
        }

        // 2. If pid exists, add Project Name
        if (query.pid && this.project && this.project.name && !path.includes('update-projects') ) {
          crumbs.push({
            text:  this.truncateText(this.project.name),
            to: {
              path: '/projects',
              query: { pid: query.pid, stype: query.stype, ptab: 'details' }
            },
            disabled: false,
          });
        }
        // If Updating a project
        if (path.includes('update-projects')) {
          crumbs.push({
            text:  this.$vuetify.lang.t("$vuetify.project.update_project"),
            to: '',
            disabled: true
          });

          if (crumbs.length > 1) {
            crumbs[crumbs.length - 2].disabled = false;
          }
        }

        // 3. If ptab exists, add it
        if (query.ptab) {
          let translatedText = query.ptab;

          switch (query.ptab) {
            case 'aiResponse':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.ai_response_tab');
              break;
            case 'designMaira':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.design_maira_tab');
              break;
            case 'playground':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.solutions_playground_tab');
              break;
            case 'details':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.project_details_tab');
              break;
            case 'settings':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.project_settings_tab');
              break;
            case 'insights':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.project_insights_tab');
              break;
            case 'setup':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.project_setup_tab');
              break;
            case 'mairaSetup':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.maira_setup_tab');
              break;
            case 'emailConfig':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.email_config_tab');
              break;
            case 'datasets':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.personalizer_datasets_tab');
              break;
            case 'mairaDatasets':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.maira_datasets_tab');
              break;
            case 'mairaProfiles':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.maira_profiles_tab');
              break;
            case 'mairaSetting':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.maira_settings_tab');
              break;
            case 'access':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.project_access_tab');
              break;
            case 'mairaQuestionnaire':
              translatedText = this.$vuetify.lang.t('$vuetify.tab.maira_questionnaire_tab');
              break;
          }

          crumbs.push({
            text: translatedText,
            to: {
              path: '/projects',
              query: { pid: query.pid, stype: query.stype, ptab: query.ptab }
            },
            disabled: false,
          });
        }

        // 4. If the path includes "create-dataset", add "Create Dataset"
        if (path.includes('create-dataset')) {
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.gpt_dataset.create_dataset'),
            to: '',
            disabled: true,
          });

          if (crumbs.length > 1) {
            crumbs[crumbs.length - 2].disabled = false;
          }
        }  else if (query.datasetId &&path.includes('update-dataset')) {
          // 5. If datasetId exists, add it
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.gpt_dataset.dataset_update'),
            to: '',
            disabled: true,
          });

          if (crumbs.length > 1) {
            crumbs[crumbs.length - 2].disabled = false;
          }
        }else if (query.datasetId) {
          // 5. If datasetId exists, add it
          crumbs.push({
            text:  this.truncateText(this.dataset.datasetName),
            to: this.$route.fullPath,
            disabled: true,
          });
        } else {
          // Fallback: mark the last item disabled if none of the above matched
          if (crumbs.length > 0) {
            crumbs[crumbs.length - 1].disabled = true;
          }
        }
      }else if (isJobPage) {
        // If on profile or account settings page
        crumbs.push({
          text: this.$vuetify.lang.t('$vuetify.job.side_nav'),
          to: { path: '/jobs' },
          disabled: false,
        });
        if (crumbs.length == 1) {
          crumbs[crumbs.length - 1].disabled = true;
        }
      } else if (isAllowedIPsPage) {
        crumbs.push({
          text: this.$vuetify.lang.t('$vuetify.allowed_ips.title'),
          to: { path: '/allowed-ips' },
          disabled: true,
        });
      } else if (isMembersPage) {
        // If on profile or account settings page
        crumbs.push({
          text: this.$vuetify.lang.t('$vuetify.member.members'),
          to: { path: '/members' },
          disabled: false,
        });

        if (query.mid) {
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.member.details'),
            to: {
              path: '/members',
              query: { mid: query.mid }
            },
            disabled: false,
          });
          if (crumbs.length > 1) { 
            crumbs[crumbs.length - 1].disabled = true;
          }
        }
        if (crumbs.length == 1) {
          crumbs[crumbs.length - 1].disabled = true;
        }
      } else if (isProfileSettingsPage) {
        // If on profile or account settings page
        crumbs.push({
          text: this.$vuetify.lang.t("$vuetify.profile.profile_settings"),
          to: "",
          disabled: true,
        });
        if (query.tab) {
          let translatedText = query.tab;

          switch (query.tab) {

            case 'basicInformation':
              translatedText = this.$vuetify.lang.t("$vuetify.profile.profile_basic_tab");
              break;
            case 'accountInformation':
              translatedText = this.$vuetify.lang.t("$vuetify.profile.profile_account_tab");
              break;
            case 'settings':
              translatedText = this.$vuetify.lang.t("$vuetify.profile.profile_settings_tab");
              break;
            case 'access_control':
              translatedText = this.$vuetify.lang.t("$vuetify.access_control.title");
              break;
          }
          crumbs.push({
            text: translatedText,
            to: {
              path: '',
              query: { mid: query.mid }
            },
            disabled: true,
          });

        }
      } else if (isCv) {
        // Default breadcrumb for other pages
        crumbs.push({
          text: 'CV',
          to: { path: '/CV' },
          disabled: false,
        });
      }else if (isNotificationsPage) {
        crumbs.push({
          text: this.$vuetify.lang.t('$vuetify.notifications.title'),
          to: { path: "/notifications" },
          disabled: true,
        });

        const notificationId =
          (this.$route.params && this.$route.params.id) ||
          (this.$route.query && this.$route.query.nid) ||
          null;

        if (notificationId) {
          const selectedNotification = (this.notifications || []).find(
            item => String(item.id) === String(notificationId)
          );

          const breadcrumbLabel =
            selectedNotification && selectedNotification.title
              ? this.truncateText(selectedNotification.title)
              : "";

          if (crumbs.length > 0) {
            crumbs[crumbs.length - 1].disabled = false;
          }

          crumbs.push({
            text: breadcrumbLabel,
            to: { name: "notification-details", params: { id: notificationId } },
            disabled: true,
          });
        }
      }

      else if (isSolutionsPage) {
        // If on profile or account settings page
        const sview = query.sview || 'allSolutions';
        const solutionText =
          sview === 'mysolutions'
            ? this.$vuetify.lang.t('$vuetify.tab.solutions_developers_tab')
            : this.$vuetify.lang.t('$vuetify.tab.solutions_users_tab');

        crumbs.push({
          text: solutionText,
          to: {
            path: '/projects',
            query: {
              stype: query.stype,
              sview:  sview,
            },
          },
          disabled: false,
        });
        if (query.sid) {
          crumbs.push({
            text: this.solution.name,
            to: { path: '/solutions', query: { sid: query.sid ,stype:query.stype ,sview: sview } },
            disabled: false,
          });
          if (crumbs.length > 1) {
            crumbs[crumbs.length - 1].disabled = true;
          }
        }
        if (path.includes('update-solutions')) {
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.solution.update_solution'),
            to: '',
            disabled: true,
          });
          if (crumbs.length > 1) {
            crumbs[crumbs.length - 2].disabled = false;
          }
        }
        if (path.includes('register-solutions')) {
          // Remove all existing crumbs (e.g., "Solutions")
          crumbs.splice(0, crumbs.length);

          // Add only "Register Solution"
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.solution.register_solution'),
            to: '',
            disabled: true,
          });
          if (crumbs.length > 1) {
            crumbs[crumbs.length - 2].disabled = true;
          }
        } 
      }
      else if (isSubscriptionPlansPage) {
        crumbs.push({
          text: this.$vuetify.lang.t('$vuetify.subscription_plans.breadcrumbs.subscription_plans'),
          to: { path: "/subscription-plans" },
          disabled: false,
        });

        if (path.includes('/create')) {
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.subscription_plans.breadcrumbs.create_plan'),
            to: "",
            disabled: true,
          });
        }

        if (path.includes('/edit')) {
          crumbs.push({
            text: this.$vuetify.lang.t('$vuetify.subscription_plans.breadcrumbs.edit_plan'),
            to: "",
            disabled: true,
          });
        }
      }
      
      if (crumbs.length == 1) {
        crumbs[crumbs.length - 1].disabled = true;
      }
      return crumbs;
    }

  },


  methods: {
    ...mapActions("account", [
      LOAD_ORGANIZATION,
      SWITCH_ACCOOUNT,
      REFRESHTOKEN,
      LOAD_ACCOUNT,
      LOGOUT,
      RESEND_VERIFICATION_EMAIL,
      LOAD_PROJECT_BY_ID,
    ]),
    ...mapActions("project", [
      LOAD_PROJECT_BY_ID,
    ]),
    ...mapActions("solution", [
      LOAD_SOLUTION_BY_ID,
    ]),

    handleSubmit(recaptcha_token, clientIP) {
      this.emailSendLoading = true;
      this.LOAD_ORGANIZATION().then(response => {
        if (response) {
          if (this.organization.email_verified) {
            this.$store.dispatch(
              alert[ADD_ALERT],
              {
                message: this.$vuetify.lang.t("$vuetify.account_already_verified"),
                color: "error",
              },
              { root: true }
            );
          } else {
            this.RESEND_VERIFICATION_EMAIL({
              email: this.organization.email,
              recaptcha_token: recaptcha_token,
              clientIP: clientIP,
            }).then(() => {
              this.emailSendLoading = false;
            }).catch(() => {
              this.emailSendLoading = false;
            });
          }
        }
      }).catch(() => {
        this.emailSendLoading = false;
      });
    },

    async handleSubmitClick() {
      const clientIP = await this.$ipCheck.getClientIP();
      const isAllowed = await this.$ipCheck.ipRangeCheck(clientIP);

      if (getEnv("VUE_APP_CAPTCHA_ENABLED") && !isAllowed) {
        this.$recaptcha
          .execute("resend_verify_email")
          .then(recaptcha_token => {
            this.handleSubmit(recaptcha_token, clientIP);
          })
          .catch(error => {
            console.error("reCaptcha error:", error);
          });
      } else {
        this.handleSubmit(null, clientIP);
      }
    },

    updateTabQuery(tabName) {
      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          ptab: tabName,
        },
      });
    },
    async loadProjectIfNeeded(pid) {  
      if (!this.project.id) {
        try {
          await this.LOAD_PROJECT_BY_ID({
            project_id: this.$route.query.pid,
          });
        } catch (error) {
          console.error("Failed to load project:", error);
        }
      }
    },
    async loadSolutionIfNeeded(sid) {
      if (!this.solution.id) {
        try {
          await this.LOAD_SOLUTION_BY_ID({
            solution_id: sid,
            localize: false
          });
        } catch (error) {
          console.error("Failed to load solution:", error);
        }
      }
    },
    truncateText(text, maxLength = 25) {
      return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
    },

  },

};
</script>

<style>
::-webkit-scrollbar-track {
  --webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #f5f5f5;
}
::-webkit-scrollbar {
  width: 10px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background-color: #757575;
}
.form-label label[for] {
  height: 20px;
  font-size: 10pt;
}
.tableHeader {
  height: 20px;
  font-size: 16px !important;
}
.singleAppbar {
  margin-top: 48px;
}
.doubleAppbar {
  margin-top: 10px;
}
.v-select__slot {
  font-size: smaller;
}
.v-text-field__slot {
  font-size: smaller;
}
</style>
